import { useState } from "react";
import { Upload, ShieldAlert, ShieldCheck, AlertTriangle, FileText, Link2, Mail, MessageSquare, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import SectionTitle from "@/components/SectionTitle";
import SEO from "@/components/SEO";

const KINDS = [
  { id: "text",  label: "MESSAGE", icon: MessageSquare, hint: "Paste the suspicious message text…" },
  { id: "email", label: "EMAIL",   icon: Mail,          hint: "Paste full email — sender, subject, body…" },
  { id: "sms",   label: "SMS",     icon: MessageSquare, hint: "Paste the SMS text…" },
  { id: "link",  label: "LINK",    icon: Link2,         hint: "https://suspicious.example.com" },
];

const RISK_META = {
  Safe:     { color: "#4ADE80", crimson: false },
  Low:      { color: "#86EFAC", crimson: false },
  Medium:   { color: "#FBBF24", crimson: false },
  High:     { color: "#FB923C", crimson: true  },
  Critical: { color: "#C1121F", crimson: true  },
};

export default function ScamDetection() {
  const [text, setText] = useState("");
  const [kind, setKind] = useState("text");
  const [imageB64, setImageB64] = useState(null);
  const [imageName, setImageName] = useState("");
  const [pdfName, setPdfName] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const activeKind = KINDS.find((k) => k.id === kind) || KINDS[0];

  const analyze = async () => {
    if (!text.trim() && !imageB64) { toast.error("Provide text or upload an image."); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/scam/analyze", { content: text, kind, image_base64: imageB64 });
      setResult(r.data);
    } catch { toast.error("Analysis failed."); }
    finally { setLoading(false); }
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/(png|jpeg|jpg|webp)$/i.test(f.type.split("/").pop())) { toast.error("PNG, JPEG or WebP only."); return; }
    const r = new FileReader();
    r.onload = () => { setImageB64(r.result.toString().split(",")[1]); setImageName(f.name); };
    r.readAsDataURL(f);
  };

  const handlePdf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".pdf")) { toast.error("PDF files only."); return; }
    setPdfName(f.name);
    setLoading(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", f);
      const r = await fetch(`${API}/scam/analyze/pdf`, { method: "POST", body: fd });
      if (!r.ok) throw new Error("PDF analysis failed");
      const data = await r.json();
      setResult(data);
      toast.success(`PDF analyzed: ${f.name}`);
    } catch { toast.error("PDF analysis failed."); }
    finally { setLoading(false); }
  };

  const clear = () => { setText(""); setImageB64(null); setImageName(""); setPdfName(""); setResult(null); };

  const risk = result ? (RISK_META[result.risk_level] || RISK_META.Medium) : null;
  const score = result?.risk_score ?? 0;
  const dialCircum = 2 * Math.PI * 90; // r=90

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-10" data-testid="scam-detection-page">
      <SEO title="Scam Analysis" description="Upload a suspicious message, email, SMS, screenshot, PDF or link. IOCRI's AI returns a risk score, red flags and safety recommendations." path="/scam-detection" image="ai" />

      <SectionTitle
        eyebrow="SIGNAL ANALYSIS · DOSSIER 04"
        title="Analyze a suspicious signal."
        subtitle="A message. A screenshot. A document. Drop it on the desk — IOCRI extracts the pattern."
      />

      {/* Command bar — kind selector + inputs */}
      <div className="mt-10 grid lg:grid-cols-[280px,1fr] gap-6">
        {/* LEFT — kind switch + upload rails */}
        <aside className="glass rounded-sm p-5 h-fit lg:sticky lg:top-40 space-y-6" data-testid="scam-command-panel">
          <div>
            <div className="text-[10px] font-mono tracking-[0.36em] text-gold mb-3">SIGNAL TYPE</div>
            <div className="space-y-1">
              {KINDS.map((k) => (
                <button
                  key={k.id}
                  data-testid={`tab-${k.id}`}
                  onClick={() => setKind(k.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-sm border text-left transition-all ${
                    kind === k.id
                      ? "border-gold/60 bg-gold/[0.06] text-white"
                      : "border-white/5 text-white/60 hover:border-gold/30 hover:text-white"
                  }`}
                >
                  <k.icon className="h-4 w-4" />
                  <span className="text-xs font-mono tracking-[0.24em]">{k.label}</span>
                  {kind === k.id && <span className="ml-auto h-1.5 w-1.5 rounded-full bg-gold" />}
                </button>
              ))}
            </div>
          </div>

          <div className="divider-gold" />

          <div>
            <div className="text-[10px] font-mono tracking-[0.36em] text-gold mb-3">EVIDENCE ATTACHMENTS</div>
            <label className="block cursor-pointer" data-testid="scam-upload-label">
              <div className="corner-brackets rounded-sm border border-dashed border-white/15 p-4 text-center hover:border-gold/50 transition-colors">
                <Upload className="mx-auto h-5 w-5 text-gold" />
                <div className="mt-2 text-white text-xs font-mono tracking-widest">SCREENSHOT · IMAGE</div>
                <div className="text-white/40 text-[10px] mt-1 font-mono">PNG · JPEG · WEBP</div>
                {imageName && <div className="mt-2 text-gold text-[11px] font-mono truncate">{imageName}</div>}
              </div>
              <input type="file" accept="image/png,image/jpeg,image/webp" hidden onChange={handleFile} data-testid="scam-file-input" />
            </label>

            <label className="block cursor-pointer mt-3" data-testid="scam-pdf-label">
              <div className="rounded-sm border border-dashed border-white/15 p-3 hover:border-gold/50 transition-colors flex items-center gap-3">
                <FileText className="h-4 w-4 text-gold shrink-0" />
                <span className="text-white/70 text-[11px] font-mono tracking-widest">PDF DOCUMENT</span>
              </div>
              <input type="file" accept="application/pdf" hidden onChange={handlePdf} data-testid="scam-pdf-input" />
            </label>
            {pdfName && <div className="mt-2 text-gold text-[11px] font-mono truncate">{pdfName}</div>}
          </div>

          <div className="divider-gold" />

          <div className="text-[10px] font-mono tracking-[0.28em] text-white/40 leading-relaxed">
            IOCRI reviews signals for known patterns. This is an educational assessment — not a legal or law-enforcement action.
          </div>
        </aside>

        {/* CENTER — Dossier scene */}
        <section className="dossier-scene min-h-[520px]" data-testid="scam-dossier-scene">
          {/* Case-file top strip */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <span className="evidence-chip crimson">Case · {String(new Date().getTime()).slice(-6)}</span>
              <span className="text-white/40 font-mono text-[10px] tracking-widest">SIGNAL: {activeKind.label}</span>
            </div>
            <div className="flex items-center gap-2">
              {(text || imageName || pdfName) && (
                <button onClick={clear} data-testid="scam-clear-btn" className="text-[10px] font-mono tracking-widest text-white/50 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 rounded-sm">
                  CLEAR
                </button>
              )}
              <Button
                onClick={analyze}
                disabled={loading}
                data-testid="scam-analyze-btn"
                className="rounded-sm bg-gold text-black hover:bg-gold-light font-mono text-[11px] tracking-[0.24em] h-10 px-5"
              >
                {loading ? "ANALYZING…" : <><Sparkles className="mr-2 h-4 w-4" /> RUN ANALYSIS</>}
              </Button>
            </div>
          </div>

          {/* The document */}
          <article
            className={`dossier-doc rounded-sm p-6 md:p-8 relative ${result ? "analyzed" : ""}`}
            data-testid="scam-dossier-doc"
          >
            {/* Header stripe */}
            <div className="flex items-center justify-between border-b border-white/5 pb-4">
              <div>
                <div className="text-[10px] font-mono tracking-[0.36em] text-gold">CLASSIFIED · SIGNAL REVIEW</div>
                <div className="font-display text-2xl md:text-3xl mt-2 text-white">
                  {result ? "Analysis complete." : "Signal awaiting review."}
                </div>
              </div>
              <div className="hidden md:flex flex-col items-end">
                <div className="text-[10px] font-mono text-white/40 tracking-widest">FILED</div>
                <div className="text-white/70 font-mono text-xs">{new Date().toISOString().slice(0,10)}</div>
              </div>
            </div>

            {/* Document body — either input or result */}
            {!result && (
              <div className="mt-6" data-testid="scam-input-area">
                <label className="text-[10px] font-mono tracking-[0.28em] text-white/50">
                  {activeKind.label} · TEXT
                </label>
                <Textarea
                  data-testid={`scam-textarea-${kind}`}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={activeKind.hint}
                  className="mt-2 min-h-[220px] bg-black/40 border border-white/10 rounded-sm text-white font-mono text-sm leading-relaxed"
                />
                {imageB64 && (
                  <div className="mt-4 rounded-sm border border-white/10 overflow-hidden max-w-xs">
                    <img src={`data:image/png;base64,${imageB64}`} alt="evidence" className="w-full opacity-90" />
                    <div className="text-[10px] font-mono text-gold px-3 py-1.5 border-t border-white/10 bg-black/40 tracking-widest">
                      EVIDENCE · {imageName}
                    </div>
                  </div>
                )}
              </div>
            )}

            {loading && (
              <div className="mt-10 text-center py-10" data-testid="scam-loading">
                <div className="inline-flex items-center gap-2">
                  <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
                </div>
                <div className="font-display text-xl mt-6 text-white">Running signal pattern analysis…</div>
                <div className="text-white/50 text-xs mt-1 font-mono tracking-widest">CROSS-CHECKING KNOWN SCAM STRUCTURES</div>
              </div>
            )}

            {/* RESULT — floating annotations */}
            {result && !loading && (
              <div className="mt-6 relative" data-testid="scam-result">
                {/* Original signal (if any) */}
                {(text || imageName) && (
                  <div className="rounded-sm border border-white/5 bg-black/40 p-4 mb-6">
                    <div className="text-[10px] font-mono tracking-[0.28em] text-white/40 mb-2">ORIGINAL SIGNAL</div>
                    <div className="text-white/70 font-mono text-xs leading-relaxed max-h-32 overflow-y-auto whitespace-pre-wrap">
                      {text || (imageName ? `[Image evidence · ${imageName}]` : "—")}
                    </div>
                  </div>
                )}

                {/* Verdict block with radial dial */}
                <div className="grid md:grid-cols-[220px,1fr] gap-6 items-center">
                  {/* Risk dial */}
                  <div className="flex justify-center relative" data-testid="scam-risk-dial">
                    <svg width="200" height="200" className="risk-dial">
                      <circle cx="100" cy="100" r="90" fill="none" strokeWidth="6" className="risk-dial-track" />
                      <circle
                        cx="100" cy="100" r="90" fill="none" strokeWidth="6"
                        className="risk-dial-value"
                        stroke={risk.color}
                        strokeDasharray={dialCircum}
                        strokeDashoffset={dialCircum * (1 - score / 100)}
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <div className="text-[10px] font-mono tracking-[0.28em] text-white/40">RISK</div>
                      <div className="font-display text-5xl leading-none mt-1" style={{ color: risk.color }}>{score}</div>
                      <div className="text-[10px] font-mono text-white/40 mt-1">/ 100</div>
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] font-mono tracking-[0.36em]" style={{ color: risk.color }}>
                      LEVEL · {result.risk_level.toUpperCase()}
                    </div>
                    <div className="font-display text-lg md:text-xl mt-2 text-white leading-snug">
                      Case verdict
                    </div>
                    <div className="mt-2 text-white/75 text-sm leading-relaxed">
                      {result.summary}
                    </div>
                  </div>
                </div>

                {/* Cyan annotations — floating callouts that physically separate */}
                <div className="mt-10 grid md:grid-cols-2 gap-4">
                  {/* Indicators — crimson side */}
                  <div>
                    <div className="text-[10px] font-mono tracking-[0.32em] mb-3" style={{ color: "#FF6B6F" }}>◤ RED FLAGS DETECTED</div>
                    <ul className="space-y-2">
                      {(result.indicators || []).map((it, i) => (
                        <li
                          key={i}
                          className="annotation relative flex gap-3 rounded-sm bg-black/40 border-l-2 px-4 py-3 text-white/85 text-sm leading-relaxed"
                          style={{ borderColor: "#C1121F", animationDelay: `${i * 100}ms` }}
                          data-testid={`indicator-${i}`}
                        >
                          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#FF6B6F" }} />
                          <span>{it}</span>
                        </li>
                      ))}
                      {(!result.indicators || result.indicators.length === 0) && (
                        <li className="text-white/50 text-sm italic">No clear indicators detected.</li>
                      )}
                    </ul>
                  </div>

                  {/* Recommendations — cyan side */}
                  <div>
                    <div className="text-[10px] font-mono tracking-[0.32em] mb-3 text-gold">SAFETY RECOMMENDATIONS ◥</div>
                    <ul className="space-y-2">
                      {(result.recommendations || []).map((it, i) => (
                        <li
                          key={i}
                          className="annotation from-right relative flex gap-3 rounded-sm bg-black/40 border-r-2 px-4 py-3 text-white/85 text-sm leading-relaxed"
                          style={{ borderColor: "#00D4FF", animationDelay: `${300 + i * 100}ms` }}
                          data-testid={`rec-${i}`}
                        >
                          <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0 text-gold" />
                          <span>{it}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Official reporting */}
                {result.reporting_resources && result.reporting_resources.length > 0 && (
                  <div className="mt-8 pt-6 border-t border-white/5">
                    <div className="text-[10px] font-mono tracking-[0.32em] text-gold mb-3">OFFICIAL REPORTING RESOURCES</div>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {result.reporting_resources.map((r, i) => (
                        <div
                          key={i}
                          className="rounded-sm border border-white/10 hover:border-gold/40 transition-colors p-4"
                          data-testid={`report-${i}`}
                        >
                          <div className="text-white font-display text-lg">{typeof r === "string" ? r : r.name}</div>
                          {typeof r === "object" && r.description && (
                            <div className="text-white/50 text-xs mt-1 font-mono leading-relaxed">{r.description}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Empty-state hint at doc bottom when nothing yet */}
            {!result && !loading && !text && !imageName && !pdfName && (
              <div className="mt-8 text-white/40 font-mono text-xs tracking-widest border-t border-white/5 pt-4 flex items-center gap-2">
                <ShieldAlert className="h-4 w-4 text-gold" />
                Paste a message on the left, attach an image or PDF, then run analysis.
              </div>
            )}
          </article>

          {/* Info footer */}
          <div className="mt-6 grid md:grid-cols-3 gap-3 text-xs">
            {[
              ["Impersonation", "Requests to act urgently or verify identity out of channel."],
              ["Financial pressure", "Wire transfers, gift cards, crypto — irreversible payments."],
              ["Emotional leverage", "Threats, romance, family emergencies, bereavement."],
            ].map(([t, d]) => (
              <div key={t} className="rounded-sm border border-white/5 bg-black/30 p-4">
                <div className="text-[10px] font-mono tracking-[0.28em] text-gold">SCAM PATTERN</div>
                <div className="text-white font-display text-base mt-1">{t}</div>
                <div className="text-white/50 mt-1">{d}</div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
